import { APIFailureContext } from "components/app/APIFailureContext";
import { useTranslate } from "hooks/useTranslate";
import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { store } from "redux/store";
import { axiosPostToken, instance } from "utils/APIHelper";
import { handleUnauthorizedAccess } from "utils/AuthUtils";
import { abovePropUserView } from "utils/CommonConstants";
import { responseTypes } from "utils/Types";
import { TOKEN_URL } from "utils/URLConstants";

function isUnauthorizedError(error: any, isTokenAPI: boolean) {
  return (
    error?.response?.status === responseTypes.unauthorized ||
    (isTokenAPI && error?.response?.status > responseTypes.success_threshold)
  );
}

export async function renewToken() {
  const response = await axiosPostToken(TOKEN_URL);
  return response;
}

//common function for 403 and 500 series err handling
const handleCommonAPIErrors = async (error: any, context: any, translate: any) => {
  if (
    error?.code === "ERR_NETWORK" ||
    (error?.response?.status > responseTypes.success_threshold &&
      error?.response?.status !== responseTypes.unauthorized)
  ) {
    const response = {
      status: error?.response?.status || responseTypes.internal_error,
      data: {
        errors: [
          {
            title: 0,
            detail:
              error?.response?.status === responseTypes.forbidden
                ? translate("user.restrictAccess")
                : await getBEMessage(error)
          }
        ]
      }
    };
    context?.setAPIError(response);
    return response;
  }
};

const getBEMessage = async (error: any) => {
  if (error?.response?.data instanceof Blob) {
    const errorText = await error.response.data?.text();
    const errorJson = JSON.parse(errorText);
    return errorJson?.errors?.[0]?.detail;
  }
  return error?.response?.data?.errors?.[0]?.detail;
};

const getConfigHeaders = (config: any) => {
  const state = store.getState();

  const url = config?.url?.split("/");
  const isTokenAPI = url?.length && url.includes("token.auth");

  const { isImpersonated, userDetails } = state.app.impersonationDetails;
  const viewAsForAbovePropUser = state.app.viewAsForAbovePropUser;
  let header: any = {
    "X-ConsumerID": localStorage.getItem("X-ConsumerID"),
    impersonateTo: "",
    "Content-Type": config.headers["Content-Type"] || "application/json"
  };
  if (viewAsForAbovePropUser === abovePropUserView["HU"]) {
    header = { ...header, profileMode: "PROFILEONPROPERTY" };
  }
  if (isImpersonated) header = { ...header, impersonateTo: userDetails.hltId };
  
  if (isTokenAPI) {
    console.log(process.env.REACT_APP_X_API_KEY);
    header = { ...header, "X-API-KEY": process.env.REACT_APP_X_API_KEY };
  } else {
    header = { ...header, Authorization: "Bearer " + localStorage.getItem("id_token") };
  }
  return header;
};

function HandleApiFailure() {
  const navigate = useNavigate();
  const translate = useTranslate();
  const context = useContext(APIFailureContext);
  const MAX_API_RETRY_COUNT = 3;
  const MAX_RENEW_TOKEN_TRY_COUNT = 3;
  let refreshingFunc: any = undefined;
  let counter = 0;
  let retryCounter = 0;
  let isRetry = false;

  useEffect(() => {
    instance.interceptors.response.use(
      (res) => {
        retryCounter = 0;
        isRetry = false;
        return res;
      },
      async (error) => {
        console.log("HandleApiFailure", error);
        const url = error?.config?.url?.split("/");

        const handleCommonRes = handleCommonAPIErrors(error, context, translate);

        if (
          error?.response?.status === responseTypes.not_found &&
          ((url?.includes("plans") && !url?.includes("sections")) || url?.includes("cards"))
        ) {
          navigate(`/`);
        }

        const isTokenAPI = url?.length && url.includes("token.auth");
        console.log("isTokenAPI", isTokenAPI);

        if (isUnauthorizedError(error, isTokenAPI) && isRetry) {
          retryCounter++;
        }

        if (counter > 1) {
          console.log("Logging out from counter check", counter);
          handleUnauthorizedAccess(true);
        }
        const token = localStorage.getItem("id_token");
        if (!token || isUnauthorizedError(error, isTokenAPI) || error?.response?.data?.error === "invalid_grant") {
          try {
            if (isTokenAPI) counter++;
            const originalConfig = error?.config;
            // the trick here, that `refreshingFunc` is global, e.g. 2 expired requests will get the same function pointer and await same function.

            const refresh = localStorage.getItem("refresh_token");

            if (refresh) {
              if (!refreshingFunc || (counter < MAX_RENEW_TOKEN_TRY_COUNT && isTokenAPI)) refreshingFunc = renewToken();
              const response = await refreshingFunc;
              if (response && response?.data) {
                const { access_token, refresh_token } = response.data;
                console.log("success response received from token endpoint");
                counter = 0;
                localStorage.setItem("id_token", access_token);
                if (refresh_token !== undefined && refresh_token) {
                  localStorage.setItem("refresh_token", refresh_token);
                }
                originalConfig.headers.Authorization = `Bearer ${access_token}`;
              } else if (counter > MAX_RENEW_TOKEN_TRY_COUNT) {
                console.log("Logging out after max get token tries.");
                handleUnauthorizedAccess(true);
              }
            }
            try {
              if (!originalConfig.url.split("/").includes("token.auth")) {
                if (!isTokenAPI && isUnauthorizedError(error, isTokenAPI) && retryCounter === MAX_API_RETRY_COUNT) {
                  console.log("Logging out due to exceeded retry attempts!", retryCounter);
                  handleUnauthorizedAccess(true);
                } else if (retryCounter < MAX_API_RETRY_COUNT) {
                  isRetry = true;
                  return await instance.request(originalConfig);
                }
              }
            } catch (innerError) {
              if (isUnauthorizedError(innerError, isTokenAPI)) {
                throw innerError;
              }
            }
          } catch (err) {
            console.log("Logging out from catch block due to below error", err);
            // handleUnauthorizedAccess();
          } finally {
            refreshingFunc = undefined;
          }
        } else {
          return error?.response?.status > responseTypes.success_threshold &&
            error?.response?.status !== responseTypes.unauthorized
            ? handleCommonRes
            : error;
        }
      }
    );

    instance.interceptors.request.use((config) => {
      config.headers = getConfigHeaders(config);
      return config;
    });
  }, [navigate]);

  return <></>;
}

export { HandleApiFailure };